<template>
  <SDialog v-model:open="modalVisible">
    <SDialogContent
      class="send-email-modal !w-[500px] !max-w-[500px]"
      @open-auto-focus.prevent
    >
      <SDialogHeader>
        <SDialogTitle>
          <h2>{{ $t("Email") }}</h2>
        </SDialogTitle>
        <SSeparator class="mb-0" />
      </SDialogHeader>

      <!-- Spinner overlay -->
      <div v-if="!bookingInfo" class="spinner-overlay">
        <LoaderCircleIcon class="s-icon-spin spinner" />
      </div>

      <form novalidate @submit.prevent="form.handleSubmit">
        <div v-if="hasInvoiceEnabled" class="mb-5">
          <p class="mb-2 text-sm font-medium">
            {{ $t("field.select-option") }}
          </p>
          <div class="flex w-full gap-3">
            <SButton
              type="button"
              class="h-[76px] flex-1 text-lg font-bold"
              :variant="
                emailType === EmailTypes.Booking ? 'default' : 'outline'
              "
              data-cy="booking-email-option"
              @click="emailType = EmailTypes.Booking"
            >
              {{ $t("button.send-booking") }}
            </SButton>
            <SButton
              type="button"
              class="h-[76px] flex-1 text-lg font-bold"
              :variant="
                emailType === EmailTypes.Invoice ? 'default' : 'outline'
              "
              data-cy="invoice-email-option"
              @click="emailType = EmailTypes.Invoice"
            >
              {{ $t("button.send-invoice") }}
            </SButton>
          </div>
        </div>
        <p v-else class="mb-5" data-cy="booking-email-option">
          {{ $t("button.send-booking") }}
        </p>

        <SSeparator class="mb-5" />

        <form.Field name="email" v-slot="{ field }">
          <SField :data-invalid="showError(field) || undefined" class="mb-4">
            <SFieldLabel :for="field.name">{{ $t("field.email") }}</SFieldLabel>
            <SInputGroup>
              <SInputGroupInput
                :id="field.name"
                :model-value="field.state.value"
                :disabled="emailType === EmailTypes.Invoice"
                :aria-invalid="showError(field) || undefined"
                :placeholder="
                  emailType === EmailTypes.Invoice && !hasCustomerEmail
                    ? $t('placeholder.no-customer')
                    : $t('field.placeholder-email')
                "
                @update:model-value="
                  (v) => field.handleChange(String(v).trim())
                "
                @blur="field.handleBlur"
              />
              <SInputGroupAddon align="inline-end">
                <SInputGroupButton
                  type="button"
                  variant="link"
                  class="font-bold"
                  @click="
                    assignOrEditCustomer(ModalStatus.SendEmail, { emailType })
                  "
                >
                  {{
                    hasCustomerEmail
                      ? $t("customers.edit")
                      : $t("asideordersummary.assign-customer")
                  }}
                </SInputGroupButton>
              </SInputGroupAddon>
            </SInputGroup>
            <SFieldError v-if="showError(field)">
              {{ firstErrorText(field) }}
            </SFieldError>
          </SField>
        </form.Field>

        <div class="mt-6 flex justify-center">
          <SButton
            type="submit"
            class="w-full"
            :loading="isSubmitting"
            :disabled="sendDisabled || isSubmitting"
          >
            {{ $t("button.send") }}
          </SButton>
        </div>
      </form>
    </SDialogContent>
  </SDialog>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted } from "vue";
import { dialogVisibleForStatus, hideDialog } from "@/utils/vue-helpers/modal";
import {
  ModalComponents,
  ModalStatus,
  SendEmailModalProps,
} from "@/constants/modal";
import {
  generateBookingInvoice,
  postBookingEmail,
  sendBookingInvoiceEmail,
} from "@/api/booking/booking";
import BookingModule from "@/store/modules/booking";
import { getBookingWithUUID } from "@/api/gql/product-slot-boooking";

// common
import { useI18n } from "vue-i18n";
import Timeout from "await-timeout";
import NotificationService from "@/services/notification.service";
import CustomersModule from "@/store/modules/customers";
import { LogLevel, LogService } from "@/services/logging";
import { handleErrorAndPrepareMessage } from "@/utils/helpers/common";
import { EmailTypes } from "@/constants/booking";
import { hasBookingChanged } from "@/utils/vue-helpers/booking";
import { assignOrEditCustomer } from "@/utils/vue-helpers/customer";
import ModalModule from "@/store/modules/modal";
import { isBookingInDexie } from "@/utils/helpers/booking";
import { hasInvoiceEnabled } from "@/utils/vue-helpers/compliance";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
  SSeparator,
  SField,
  SFieldLabel,
  SFieldError,
  SInputGroup,
  SInputGroupInput,
  SInputGroupAddon,
  SInputGroupButton,
} from "@smeetz/ui-core";
import { LoaderCircleIcon } from "@smeetz/ui-icons";

import { useSmeetzForm } from "@smeetz/services-validation";
import { sendEmailSchema, type SendEmailValues } from "./send-email.form";

defineOptions({ name: ModalComponents.SendEmail });

const props = defineProps<{
  modalProps: {
    referrer?: ModalComponents;
    goBack?: boolean;
    props?: SendEmailModalProps;
  };
}>();

const modalVisible = dialogVisibleForStatus(ModalStatus.SendEmail);
const { t } = useI18n();

const emailType = ref<EmailTypes>(
  props.modalProps.props?.emailType || EmailTypes.Booking
);
const hasCustomerEmail = computed(
  () => CustomersModule.getSelectedCustomerEmail
);
const bookingInfo = ref<any>(null);

const { form, showError, firstErrorText } = useSmeetzForm<SendEmailValues>({
  defaultValues: { email: CustomersModule.getSelectedCustomerEmail || "" },
  schema: sendEmailSchema(),
  validatorTiming: "onSubmit",
  onSubmit: async ({ value }) => {
    await doSend(value.email);
  },
});

const isSubmitting = form.useStore((state) => state.isSubmitting);
const emailValue = form.useStore((state) => state.values.email);

const sendDisabled = computed(
  () =>
    (emailType.value === EmailTypes.Booking && !emailValue.value) ||
    (emailType.value === EmailTypes.Invoice && !hasCustomerEmail.value)
);

// Reset the address to the selected customer's email whenever the email type
// switches (the invoice flow always emails the customer on file).
watch(emailType, () => {
  form.setFieldValue("email", CustomersModule.getSelectedCustomerEmail || "");
});

/**
 * Send the booking or invoice email for the synced booking.
 *
 * @param {string} email recipient address.
 * @returns {Promise<void>}
 */
async function doSend(email: string) {
  const info = bookingInfo.value;
  try {
    if (emailType.value === EmailTypes.Invoice) {
      // if the booking does not have an invoice yet, or it has been updated
      // we need to generate a new invoice
      const isEditOrder = await isBookingInDexie(info.uid);
      const bookingChanged = await hasBookingChanged(
        BookingModule.selectedBooking
      );
      if (!info?.invoiceId || (isEditOrder && bookingChanged)) {
        LogService.info(
          `Going to generate an invoice for the booking uuid: ${info.uid}`
        );
        await generateBookingInvoice(Number(info?.id));
      }
      LogService.info(
        `Emailing the booking inovice, booking uuid ${info.uid} to the user email address ${email}`
      );
      await sendBookingInvoiceEmail(Number(info?.id), 1);
    } else {
      LogService.info(
        `Emailing the booking uuid ${info.uid} to the user email address ${email}`
      );
      await postBookingEmail({
        email,
        bookingId: info?.id,
        first_name: CustomersModule.getSelectedCustomerFirstName,
        last_name: CustomersModule.getSelectedCustomerLastName,
      });
    }
    hideDialog();
    NotificationService.showCustomSuccess(
      t("completeorder.email-tickets-success")
    );
  } catch (err: any) {
    const errorDetails = handleErrorAndPrepareMessage(err);
    LogService.info(
      `${errorDetails.errorType} Error when emailing booking with uuid: ${info?.uid}`,
      { params: { err, errorDetails } },
      LogLevel.Error
    );
    NotificationService.showCustomMessage(errorDetails.errorMessage, "error");
  }
}

onMounted(async () => {
  // keep showing the spinner until the booking gets synced
  const uid = BookingModule.selectedBookingUUID;
  if (!uid) {
    throw new Error("No uuid for this booking");
  }
  let info;
  let retries = 0;
  while (!info && retries < 5) {
    info = await getBookingWithUUID(uid);
    LogService.info(
      `Booking with uuid: ${uid} was not found when sending email, retrying in 3000ms`
    );
    retries++;
    await Timeout.set(3000);
  }
  if (!info) {
    // synching failed
    LogService.info(
      `Booking with uuid: ${uid} was not found when sending email, stopped retrying`
    );
    NotificationService.showGeneralResponseError();
    ModalModule.setStatus(ModalStatus.Hidden);
    return;
  }
  bookingInfo.value = info;
});
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;
.send-email-modal {
  position: relative;
}

.spinner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;

  .spinner {
    font-size: 48px;
    color: $color-dark-cyan;
  }
}
</style>
